import LockResetIcon from "@mui/icons-material/LockReset";
import { Alert, Box, Paper, Typography } from "@mui/material";
import {
  Button,
  Field,
  FieldsContainer,
  globalMessageHandler,
  PasswordField,
} from "common";
import authService from "features/auth/authApi";
import I18n from "i18n";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [token, setToken] = useState(searchParams.get("token") || "");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  useEffect(() => {
    // Extract token and email from URL parameters
    const urlToken = searchParams.get("token");
    const urlEmail = searchParams.get("email");

    if (urlToken) setToken(urlToken);
    if (urlEmail) setEmail(urlEmail);
  }, [searchParams]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!email) {
      newErrors.email = I18n.t("Auth.resetPassword.errors.emailRequired");
    }
    if (!token) {
      newErrors.token = I18n.t("Auth.resetPassword.errors.tokenRequired");
    }
    if (!password) {
      newErrors.password = I18n.t("Auth.resetPassword.errors.passwordRequired");
    }
    if (password && password.length < 8) {
      newErrors.password = I18n.t(
        "Auth.resetPassword.errors.passwordMinLength"
      );
    }
    if (!passwordConfirmation) {
      newErrors.password_confirmation = I18n.t(
        "Auth.resetPassword.errors.passwordConfirmationRequired"
      );
    }
    if (password !== passwordConfirmation) {
      newErrors.password_confirmation = I18n.t(
        "Auth.resetPassword.errors.passwordsDoNotMatch"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const {
        success,
        data,
        error: apiError,
      } = await authService.resetPassword({
        email: email.trim(),
        token: token.trim(),
        password,
        password_confirmation: passwordConfirmation,
      });

      if (success) {
        setSuccess(true);
        globalMessageHandler.snack({
          message: data?.message || I18n.t("Auth.resetPassword.success"),
          severity: "success",
        });

        // Redirect to login after a brief delay
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        const errorMessage =
          apiError?.error?.message ||
          I18n.t("Auth.resetPassword.errors.failedToReset");
        if (apiError?.error?.errors) {
          // Handle validation errors from backend
          setErrors(apiError.error.errors);
        } else {
          setErrors({ general: errorMessage });
        }
        globalMessageHandler.snack({
          message: errorMessage,
          severity: "error",
        });
      }
    } catch (err) {
      const errorMessage = I18n.t("Auth.resetPassword.errors.networkError");
      setErrors({ general: errorMessage });
      globalMessageHandler.snack({
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          margin: "0 auto",
          mt: 4,
          textAlign: "center",
        }}
      >
        <LockResetIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {I18n.t("Auth.resetPassword.successTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {I18n.t("Auth.resetPassword.successMessage")}
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            onClick={() => navigate("/login")}
            variant="contained"
            fullWidth
          >
            {I18n.t("Auth.resetPassword.goToLogin")}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 400,
        margin: "0 auto",
        mt: 4,
      }}
    >
      <Typography variant="h4" gutterBottom textAlign="center">
        {I18n.t("Auth.resetPassword.title")}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        paragraph
        textAlign="center"
      >
        {I18n.t("Auth.resetPassword.subtitle")}
      </Typography>

      {errors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.general}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <FieldsContainer>
          <Field fullWidth>
            <PasswordField
              label={I18n.t("Auth.resetPassword.newPasswordLabel")}
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              autoFocus
              disabled={loading}
              error={errors.password}
              helperText={errors.password}
            />
          </Field>
          <Field fullWidth>
            <PasswordField
              label={I18n.t("Auth.resetPassword.confirmPasswordLabel")}
              value={passwordConfirmation}
              onChange={(e: any) => setPasswordConfirmation(e.target.value)}
              required
              autoComplete="new-password"
              disabled={loading}
              error={errors.password_confirmation}
              helperText={errors.password_confirmation}
            />
          </Field>
        </FieldsContainer>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={loading || !email || !token}
          >
            {I18n.t("Auth.resetPassword.resetButton")}
          </Button>
        </Box>

        <Box textAlign="center">
          <Typography variant="body2">
            {I18n.t("Auth.resetPassword.rememberPassword")}{" "}
            <Link
              to="/login"
              style={{
                color: "inherit",
                textDecoration: "underline",
              }}
            >
              {I18n.t("Auth.resetPassword.backToLogin")}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
